import React, { useState, useEffect, useCallback, useRef } from 'react';
import BannerUi from './BannerUI';
import { getCurrentBannerIndex, setCurrentBannerIndex } from './BannersStorage';
import { useBannersContext } from './BannersContext';

const isTouch = true; //'ontouchstart' in window || navigator.msMaxTouchPoints > 0;

const Banner = (props: { section?: string }) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [prevIndex, setPrevIndex] = useState<number>(0);
  const [showArrow, setShowArrow] = useState(isTouch);
  const [animateType, setAnimateType] = useState<'slideInLeft' | 'slideInRight' | false>(false);
  const timerRef: { current: number | null } = useRef(null);
  const { banners: items } = useBannersContext({ section: props.section });

  const setAnimate = (nextIndex: number, currentIndex: number) => {
    if (typeof currentIndex !== 'number' || typeof nextIndex !== 'number' || nextIndex === currentIndex) {
      setAnimateType(false);
      return;
    }
    setAnimateType(nextIndex < currentIndex ? 'slideInLeft' : 'slideInRight');
  };

  const setNextPageTransitionTimeout = (callback: () => void) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(callback, 5000);
  };

  const handleSetCurrentIndex = useCallback(
    (index: number) => {
      const nextIndex = index > items.length - 1 || index < 0 ? 0 : index;
      setPrevIndex(currentIndex);
      setAnimate(index, currentIndex);
      setCurrentIndex(nextIndex);
    },
    [currentIndex, items.length],
  );

  useEffect(() => {
    const lastBannerDisplayedIndex = getCurrentBannerIndex();
    handleSetCurrentIndex(typeof lastBannerDisplayedIndex === 'number' ? lastBannerDisplayedIndex + 1 : 0);
  }, []);

  const onClickLeft = useCallback(() => {
    handleSetCurrentIndex(currentIndex - 1);
  }, [handleSetCurrentIndex, currentIndex]);

  const onClickRight = useCallback(() => {
    handleSetCurrentIndex(currentIndex + 1);
  }, [handleSetCurrentIndex, currentIndex]);

  const onClickPoint = (e: React.MouseEvent<HTMLDivElement>) => {
    const pointIndex = e.currentTarget.dataset['pointIndex'];
    if (pointIndex) {
      handleSetCurrentIndex(parseInt(pointIndex));
    }
  };

  useEffect(() => {
    setNextPageTransitionTimeout(onClickRight);
  }, [currentIndex, onClickRight]);

  const onMouseEnter = () => {
    setShowArrow(true);
  };
  const onMouseLeave = () => {
    setShowArrow(false);
  };
  const calcProps: { onMouseEnter?: () => void; onMouseLeave?: () => void } = {};

  if (!isTouch) {
    calcProps.onMouseEnter = onMouseEnter;
    calcProps.onMouseLeave = onMouseLeave;
  }

  useEffect(() => {
    if (typeof currentIndex === 'number') setCurrentBannerIndex(currentIndex);
  }, [currentIndex]);

  return (
    <BannerUi
      showArrow={showArrow}
      currentIndex={currentIndex}
      prevIndex={prevIndex}
      animateType={animateType}
      items={items}
      isTouch={isTouch}
      onClickLeft={onClickLeft}
      onClickRight={onClickRight}
      onClickPoint={onClickPoint}
      {...calcProps}
    ></BannerUi>
  );
};

export default Banner;
